import { Immutable } from "immer";
import React from "react";
import { ProductGridSSR } from "tsi-common-react/src/apps/product-grid2/ProductGridSSR/index";
import { BannerBlock } from "tsi-common-react/src/components/BannerBlock/index";
import { PromoConditionalBlock } from "tsi-common-react/src/components/PromoConditionalBlock/index";

import { templates } from "@reactivated";
import type {
    PillowsBannerPromoConditionalBlock,
    PillowsPromoConditionalBlock,
    PillowsToolboxPromoConditionalBlock,
} from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { DiscoverTempurLoveBlock } from "../../blocks/DiscoverTempurLoveBlock";
import { InsideInnovationBlock } from "../../blocks/InsideInnovationBlock";
import { OfferSectionBlock } from "../../blocks/OfferSectionBlock";
import { SidekickBlock } from "../../blocks/SidekickBlock";
import {
    pillowsPromoConditionalInnerMap,
    toolboxComponentsMap,
} from "../../blocks/toolboxComponentsMap";

import categoryListStyles from "../../styles/composable/category-list.module.scss";
import heroStyles from "../../styles/composable/product-list-hero.module.scss";
import styles from "./index.module.scss";

/**
 * Component map for the pillows ProductFilterGridPage body. Exported so the
 * block-screenshot styleguide harness renders through the same map production
 * uses (see ``StyleguideBlockTemplate.tsx``).
 */
export const productFilterGridPageBodyComponentsMap = {
    ...toolboxComponentsMap,
    // PillowsToolboxPromoConditionalBlock: content is a ToolboxContent
    // StreamBlock, dispatched through the same toolboxComponentsMap.
    promo_conditional_block: (block: {
        value: Immutable<PillowsToolboxPromoConditionalBlock>;
    }) => (
        <PromoConditionalBlock value={block.value}>
            <StreamField
                tagName={React.Fragment}
                value={block.value.content ?? []}
                components={toolboxComponentsMap}
            />
        </PromoConditionalBlock>
    ),
    // PillowsPromoConditionalBlock: content is popular_products + banner.
    promo_conditional: (block: {
        value: Immutable<PillowsPromoConditionalBlock>;
    }) => (
        <PromoConditionalBlock value={block.value}>
            <StreamField
                tagName={React.Fragment}
                value={block.value.content ?? []}
                components={pillowsPromoConditionalInnerMap}
            />
        </PromoConditionalBlock>
    ),
    offer_section: OfferSectionBlock,
    innovation_inside: InsideInnovationBlock,
    discover_tempur_love: DiscoverTempurLoveBlock,
    product_grid: (block: {
        value: React.ComponentProps<typeof ProductGridSSR> | null;
    }) => (block.value ? <ProductGridSSR {...block.value} /> : null),
} as const;

const ProductFilterGridPageStreamField = (
    props: Immutable<{ page: templates.ProductFilterGridPageTemplate["page"] }>,
): React.JSX.Element | null => {
    if (!props.page.body) {
        return null;
    }
    return (
        <StreamField
            tagName={React.Fragment}
            value={props.page.body.value}
            components={productFilterGridPageBodyComponentsMap}
        />
    );
};

/**
 * Pillows ProductFilterGridPage hero-sidekick assembly: dispatches the
 * ``ProductFilterGridHeroSidekickBlock`` StreamField to its React component,
 * replacing the legacy ``{% for block in self.hero_sidekick %}
 * {% include_block block %}{% endfor %}`` hydration roots.
 */
const ProductFilterGridHeroSidekick = (
    props: Immutable<{ page: templates.ProductFilterGridPageTemplate["page"] }>,
): React.JSX.Element | null => {
    if (!props.page.hero_sidekick) {
        return null;
    }
    return (
        <StreamField
            tagName={React.Fragment}
            value={props.page.hero_sidekick.value}
            components={{
                hero_sidekick: SidekickBlock,
            }}
        />
    );
};

/**
 * Pillows ProductFilterGridPage top-banner assembly: dispatches the
 * ``ProductFilterGridTopBannerBlock`` StreamField to the shared
 * ``BannerBlock`` component, replacing the legacy
 * ``{% for block in self.top_banner %}{% include_block block %}{% endfor %}``
 * hydration roots.
 */
const ProductFilterGridTopBanner = (
    props: Immutable<{ page: templates.ProductFilterGridPageTemplate["page"] }>,
): React.JSX.Element | null => {
    if (!props.page.top_banner) {
        return null;
    }
    return (
        <StreamField
            tagName={React.Fragment}
            value={props.page.top_banner.value}
            components={{
                banner: BannerBlock,
                promo_conditional_block: (block: {
                    value: Immutable<PillowsBannerPromoConditionalBlock>;
                }) => (
                    <PromoConditionalBlock value={block.value}>
                        <StreamField
                            tagName={React.Fragment}
                            value={block.value.content ?? []}
                            components={{
                                banner: BannerBlock,
                            }}
                        />
                    </PromoConditionalBlock>
                ),
            }}
        />
    );
};

type IHeroContentProps = Immutable<{
    className: string;
    preTitle: string | null;
    titleHtml: string;
    description: string | null;
    linkCopy: string | null;
    linkUrl: string;
    shopAllCopy: string | null;
    shopAllUrl: string;
}>;

const HeroContent = (props: IHeroContentProps): React.JSX.Element => (
    <div className={props.className}>
        {props.preTitle && (
            <h4 className={heroStyles.productListHeroCategory}>
                {props.preTitle}
            </h4>
        )}
        <h1 className={heroStyles.productListHeroTitle}>
            <div
                className="rich-text"
                dangerouslySetInnerHTML={{ __html: props.titleHtml }}
            />
        </h1>
        {props.description && (
            <p
                className={concatClassNames([
                    heroStyles.productListHeroDescription,
                    heroStyles.productListHeroDescriptionPillows,
                ])}
            >
                {props.description}
            </p>
        )}
        {props.linkCopy && (
            <div className={heroStyles.productListHeroButton}>
                <a href={props.linkUrl} className="button" role="button">
                    {props.linkCopy}
                </a>
            </div>
        )}
        {props.shopAllCopy && (
            <a href={props.shopAllUrl}>
                <span>{props.shopAllCopy}</span>
            </a>
        )}
    </div>
);

export const ProductFilterGridPageBody = (
    props: Immutable<templates.ProductFilterGridPageTemplate>,
): React.JSX.Element => {
    const {
        page,
        hero_image_url,
        hero_title_html,
        hero_title_mobile_html,
        hero_link_url,
        shop_all_link_url,
        hero_badge_image_url,
        hero_badge_image_alt,
    } = props;

    return (
        <div className="l-full-width">
            <div
                className={concatClassNames([
                    "hero",
                    heroStyles.productListHero,
                    heroStyles.productListHeroPillows,
                    "l-full-width",
                ])}
                style={
                    hero_image_url
                        ? { backgroundImage: `url(${hero_image_url})` }
                        : undefined
                }
            >
                <div
                    className={concatClassNames([
                        "l-capped-width",
                        heroStyles.productListHeroFilterGridHeroWrapper,
                    ])}
                >
                    {hero_badge_image_url && (
                        <div
                            className={concatClassNames([
                                heroStyles.productListHeroBadgeImageContainer,
                                heroStyles.productListHeroBadgeImageContainerPillows,
                            ])}
                        >
                            <div>
                                <img
                                    className="responsive-img"
                                    src={hero_badge_image_url}
                                    alt={hero_badge_image_alt}
                                />
                            </div>
                        </div>
                    )}
                    <div className={heroStyles.productListHeroInner}>
                        <HeroContent
                            className={heroStyles.productListHeroContentPillows}
                            preTitle={page.hero_pre_title}
                            titleHtml={hero_title_html}
                            description={page.hero_description}
                            linkCopy={page.hero_link_copy}
                            linkUrl={hero_link_url}
                            shopAllCopy={page.shop_all_link_copy}
                            shopAllUrl={shop_all_link_url}
                        />
                        <HeroContent
                            className={concatClassNames([
                                heroStyles.productListHeroContentPillows,
                                heroStyles.productListHeroContentPillowsMobile,
                            ])}
                            preTitle={
                                page.hero_pre_title_mobile ||
                                page.hero_pre_title
                            }
                            titleHtml={
                                hero_title_mobile_html || hero_title_html
                            }
                            description={
                                page.hero_description_mobile ||
                                page.hero_description
                            }
                            linkCopy={page.hero_link_copy}
                            linkUrl={hero_link_url}
                            shopAllCopy={page.shop_all_link_copy}
                            shopAllUrl={shop_all_link_url}
                        />
                    </div>
                </div>
            </div>

            <ProductFilterGridHeroSidekick page={page} />

            <ProductFilterGridTopBanner page={page} />

            {page.category_title && (
                <div
                    className={concatClassNames([
                        categoryListStyles.categoryListHeader,
                        styles.pillowsIndexHeader,
                        "l-capped-width",
                    ])}
                >
                    <div className={styles.pillowsIndexHeaderCopy}>
                        <div
                            className={
                                categoryListStyles.categoryListHeaderTitle
                            }
                        >
                            {page.category_title}
                        </div>
                        <div>{page.category_sub_title}</div>
                    </div>
                </div>
            )}

            <ProductFilterGridPageStreamField page={page} />
        </div>
    );
};
